import { Fields, DefaultComponentProps, useGetPuck } from '@measured/puck';
import { mandatoryComponents } from 'shared-components';
import { IBorderValueProps } from '../puck/reusable-props/Border';
import { MarginType } from '../puck/reusable-props/margin';
import { MarginType as NewMarginType } from '../puck/reusable-props/NewMargin';
import { PaddingType } from '../puck/reusable-props/padding';
import { OrderAndUpSellSummary } from '../interface/commonInterface';
import { StoreProps } from '../services/constants';
import { options } from './constants';
import { get } from 'lodash';
import { IColumnsProps } from '../puck/reusable-props/Column';
import { ImageBorderValueProps } from '../puck/reusable-props/ImageBorder';
import { getCurrencySymbol, getCurrencyWithSymbol } from './formatPrice';
import { Cart, ICalculatedFee } from '../interface/checkoutInterface';
import { ChangeEvent } from 'react';
import { stateList } from '../data/country';

type BorderKey =
  | 'borderTopRight'
  | 'borderTopLeft'
  | 'borderBottomLeft'
  | 'borderBottomRight'
  | 'borderTopWidth'
  | 'borderRightWidth'
  | 'borderBottomWidth'
  | 'borderLeftWidth';

type ImageBorderKey =
  | 'borderTopRight'
  | 'borderTopLeft'
  | 'borderBottomLeft'
  | 'borderBottomRight';

export const viewPorts = {
  mobile: 450,
  tablet: 1023,
  desktop: 1280,
  largeDesktop: 1536,
};

export const devices = {
  mobile: 'mobile' as const,
  tablet: 'tablet' as const,
  desktop: 'desktop' as const,
};

export const resolveShowComponentField = <Props extends DefaultComponentProps>(
  id: string,
  fields: Fields<Props>,
) => {
  const template = new URLSearchParams(window.location.search).get('template');
  const [componentName] = id.split('-');

  if (template && mandatoryComponents?.[template]?.includes(componentName)) {
    if (fields?.showComponent) {
      delete fields.showComponent;
    }
  }
  return fields;
};

export const getcurrentDeviceType = (
  windowWidth: number,
  editMode: boolean | undefined,
) => {
  let width = windowWidth;
  if (editMode) {
    const getPuck = useGetPuck();
    const { appState } = getPuck();
    width = appState?.ui?.viewports?.current?.width;
  }

  if (width <= viewPorts.mobile) {
    return devices.mobile;
  } else if (width > viewPorts.mobile && width <= viewPorts.tablet) {
    return devices.tablet;
  } else {
    return devices.desktop;
  }
};

export const getNewCurrentDeviceType = (
  windowWidth: number,
  editMode: boolean | undefined,
) => {
  let width = windowWidth;
  if (editMode) {
    const getPuck = useGetPuck();
    const { appState } = getPuck();
    width = appState?.ui?.viewports?.current?.width;
  }

  if (width <= viewPorts.mobile) {
    return devices.mobile;
  } else if (width > viewPorts.mobile && width <= viewPorts.tablet) {
    return devices.tablet;
  } else if (width > viewPorts.tablet && width <= viewPorts.desktop) {
    return devices.desktop;
  } else if (width > viewPorts.desktop && width <= viewPorts.largeDesktop) {
    return 'doubleXL';
  } else {
    return 'tripleXL';
  }
};

export const getBorderProps = (
  border: IBorderValueProps,
  currentDeviceType: string,
  isOld?: boolean,
) => {
  const isOldBorder = isOld ?? false;
  const getBorderValue = (key: BorderKey) => {
    const value = border?.[key]?.[currentDeviceType];
    return value && isNaN(+value) ? value : value && +value;
  };

  return {
    borderTopRightRadius: getBorderValue('borderTopRight'),
    borderTopLeftRadius: getBorderValue('borderTopLeft'),
    borderBottomLeftRadius: getBorderValue('borderBottomLeft'),
    borderBottomRightRadius: getBorderValue('borderBottomRight'),
    borderColor: border?.borderColor?.[currentDeviceType],
    ...(isOldBorder
      ? {
          borderTopWidth: getBorderValue('borderTopWidth'),
          borderRightWidth: getBorderValue('borderRightWidth'),
          borderBottomWidth: getBorderValue('borderBottomWidth'),
          borderLeftWidth: getBorderValue('borderLeftWidth'),
        }
      : {
          borderWidth: border?.borderWidth?.[currentDeviceType],
        }),
  };
};

export const getBorderRadiusProps = (borderRadius: {
  borderTopLeft: string;
  borderTopRight: string;
  borderBottomLeft: string;
  borderBottomRight: string;
}) => {
  return {
    borderTopLeftRadius: borderRadius?.borderTopLeft
      ? isNaN(+borderRadius.borderTopLeft)
        ? borderRadius.borderTopLeft
        : `${+borderRadius.borderTopLeft}px`
      : '0px',
    borderTopRightRadius: borderRadius?.borderTopRight
      ? isNaN(+borderRadius.borderTopRight)
        ? borderRadius.borderTopRight
        : `${+borderRadius.borderTopRight}px`
      : '0px',
    borderBottomLeftRadius: borderRadius?.borderBottomLeft
      ? isNaN(+borderRadius.borderBottomLeft)
        ? borderRadius.borderBottomLeft
        : `${+borderRadius.borderBottomLeft}px`
      : '0px',
    borderBottomRightRadius: borderRadius?.borderBottomRight
      ? isNaN(+borderRadius.borderBottomRight)
        ? borderRadius.borderBottomRight
        : `${+borderRadius.borderBottomRight}px`
      : '0px',
  };
};

export const getImageBorderProps = (
  imageBorder: ImageBorderValueProps,
  currentDeviceType: string,
) => {
  const getBorderValue = (key: ImageBorderKey) => {
    const value = imageBorder?.[key]?.[currentDeviceType];
    return value && isNaN(+value) ? value : value && +value;
  };

  return {
    borderTopRightRadius: getBorderValue('borderTopRight'),
    borderTopLeftRadius: getBorderValue('borderTopLeft'),
    borderBottomLeftRadius: getBorderValue('borderBottomLeft'),
    borderBottomRightRadius: getBorderValue('borderBottomRight'),
  };
};

export const getPaddingStyles = (
  padding: PaddingType,
  currentDeviceType: string,
) => {
  return {
    paddingLeft: isNaN(+padding?.paddingLeft?.[currentDeviceType])
      ? padding?.paddingLeft?.[currentDeviceType]
      : +padding?.paddingLeft?.[currentDeviceType],
    paddingRight: isNaN(+padding?.paddingRight?.[currentDeviceType])
      ? padding?.paddingRight?.[currentDeviceType]
      : +padding?.paddingRight?.[currentDeviceType],
    paddingTop: isNaN(+padding?.paddingTop?.[currentDeviceType])
      ? padding?.paddingTop?.[currentDeviceType]
      : +padding?.paddingTop?.[currentDeviceType],
    paddingBottom: isNaN(+padding?.paddingBottom?.[currentDeviceType])
      ? padding?.paddingBottom?.[currentDeviceType]
      : +padding?.paddingBottom?.[currentDeviceType],
  };
};

export const getPaddingStylesForColumns = (
  column: IColumnsProps,
  currentDeviceType: keyof typeof devices,
) => {
  return {
    paddingLeft: isNaN(+column?.padding?.paddingLeft?.[currentDeviceType])
      ? column?.padding?.paddingLeft?.[currentDeviceType]
      : +column?.padding?.paddingLeft?.[currentDeviceType],
    paddingRight: isNaN(+column?.padding?.paddingRight?.[currentDeviceType])
      ? column?.padding?.paddingRight?.[currentDeviceType]
      : +column?.padding?.paddingRight?.[currentDeviceType],
    paddingTop: isNaN(+column?.padding?.paddingTop?.[currentDeviceType])
      ? column?.padding?.paddingTop?.[currentDeviceType]
      : +column?.padding?.paddingTop?.[currentDeviceType],
    paddingBottom: isNaN(+column?.padding?.paddingBottom?.[currentDeviceType])
      ? column?.padding?.paddingBottom?.[currentDeviceType]
      : +column?.padding?.paddingBottom?.[currentDeviceType],
  };
};
export function getMarginStyles(
  margin: MarginType,
  currentDeviceType: keyof MarginType,
) {
  return {
    marginLeft: margin?.[currentDeviceType]?.left?.includes('%')
      ? margin?.[currentDeviceType]?.left
      : margin?.[currentDeviceType]?.left + 'px',
    marginRight: margin?.[currentDeviceType]?.right?.includes('%')
      ? margin?.[currentDeviceType]?.right
      : margin?.[currentDeviceType]?.right + 'px',
    marginTop: margin?.[currentDeviceType]?.top?.includes('%')
      ? margin?.[currentDeviceType]?.top
      : margin?.[currentDeviceType]?.top + 'px',
    marginBottom: margin?.[currentDeviceType]?.bottom?.includes('%')
      ? margin?.[currentDeviceType]?.bottom
      : margin?.[currentDeviceType]?.bottom + 'px',
  };
}

export function getNewMarginStyles(
  margin: NewMarginType,
  currentDeviceType: string,
) {
  return {
    marginLeft: isNaN(+margin?.marginLeft?.[currentDeviceType])
      ? margin?.marginLeft?.[currentDeviceType]
      : +margin?.marginLeft?.[currentDeviceType],
    marginRight: isNaN(+margin?.marginRight?.[currentDeviceType])
      ? margin?.marginRight?.[currentDeviceType]
      : +margin?.marginRight?.[currentDeviceType],
    marginTop: isNaN(+margin?.marginTop?.[currentDeviceType])
      ? margin?.marginTop?.[currentDeviceType]
      : +margin?.marginTop?.[currentDeviceType],
    marginBottom: isNaN(+margin?.marginBottom?.[currentDeviceType])
      ? margin?.marginBottom?.[currentDeviceType]
      : +margin?.marginBottom?.[currentDeviceType],
  };
}

export const getNumberPattern = (
  inputValue: string,
  isNegativeValidation: boolean = false,
): string | null => {
  if (isNegativeValidation) {
    if (inputValue === '' || inputValue === '-') return inputValue;
    else if (inputValue === '-0') return '0';

    if (/^-?\d+(?:%|px|)?$/.test(inputValue)) {
      inputValue = inputValue.replace(/^-?0+(?=\d)/, match =>
        match.startsWith('-') ? '-' : '',
      );
      return inputValue;
    }

    return inputValue.substring(0, inputValue.length - 1);
  }

  if (inputValue === '') {
    return '0';
  }
  if (/^[0-9]+(?:%|px|)?$/.test(inputValue)) {
    inputValue = inputValue.replace(/^0+(?=\d)/, '');
    return inputValue;
  }
  return null;
};
export const getNumberPatternWithAuto = (inputValue: string): string | null => {
  let newValue = inputValue.replace(/[^0-9%]/g, '');

  if (newValue.includes('%')) {
    newValue = newValue.replace(/%.*%/g, '%').replace(/%/g, '') + '%';
  }

  if (!newValue.trim()) {
    newValue = 'auto';
  }

  if (/\d/.test(newValue) && /%/.test(newValue)) {
    newValue = newValue.replace(/[^0-9%]/g, '');
  }

  if (!newValue.trim()) {
    newValue = 'auto';
  }
  return newValue;
};

export const getSpanPercentage = (value: string) => {
  if (!value) {
    return 'auto';
  }
  let newValue = value?.replace(/[^0-9.%]/g, ''); // remove all invalid characters
  newValue = newValue?.replace(/^(\d+(\.\d+)?)(%?)$/, '$1$3'); // keep number and one optional %

  // Enforce: '%' only allowed once and only at the end
  if (/%/.test?.(newValue)) {
    // If % is not at the end, remove it
    if (!newValue?.endsWith?.('%')) {
      newValue = newValue?.replace(/%/g, '');
    } else {
      // Remove all % except the last one
      const parts = newValue?.split?.('%');
      newValue = parts?.[0]?.replace?.(/%/g, '') + '%';
    }
  }

  return newValue;
};

export const getQuillEditorText = (text: string, storeData: StoreProps) => {
  if (!storeData || !text || typeof text !== 'string') return text;
  const countriesList = storeData?.countryList?.countriesList;
  const interpolatedText = text?.replace(/\{(\w+)\}/g, (_, key) => {
    const val = options?.find(option => option.key === key)?.value || '';

    const data: string | object | Array<object> | number = get(
      storeData,
      val,
      '',
    );
    if (key === 'hostname') {
      return window.location.hostname || '';
    }
    if (key === 'path') {
      return window.location.href || '';
    }
    if (key === 'country') {
      const country = countriesList?.find(country => country?.code === data);
      return country?.name || data || '';
    }
    if (key === 'state') {
      const country = countriesList?.find(
        country =>
          country?.code ===
          get(storeData, 'cart.cartData.ShippingAddress.CountryCode', ''),
      );
      const state = country?.provinces?.find(state => state?.code === data);
      return state?.name || data || '';
    }

    if (key === 'products_orders') {
      if (Array.isArray(data)) {
        const product = data?.reduce(
          (acc: string[], item: OrderAndUpSellSummary) => {
            item.products?.forEach(item => {
              acc.push(item?.productName);
            });
            return acc;
          },
          [],
        );
        return product?.join(', ') || '';
      }
    }

    if (key === 'products_orders_price') {
      if (Array.isArray(data)) {
        const product = data?.reduce(
          (acc: number, item: OrderAndUpSellSummary) => {
            return (
              acc +
              item.products?.reduce(
                (acc, item) => acc + item?.quantity * item?.productPrice,
                0,
              )
            );
          },
          0,
        );
        return product;
      }
    }

    if (key === 'upsell_product_image') {
      return storeData?.postUpSellState?.selectedVariant?.ImageURL;
    }
    if (key === 'shipping_method') {
      if (Array.isArray(data)) {
        return (
          data?.find(
            item => item?.ID === storeData?.cart?.cartData?.ShippingMethodID,
          )?.Name || ''
        );
      }
    }
    if (key === 'total_quantity_in_cart') {
      if (Array.isArray(data)) {
        const totalQuantity = data?.reduce(
          (acc, item) => acc + item?.Quantity || 0,
          0,
        );
        return totalQuantity;
      }
    }
    if (key === 'sub_total' || key === 'grand_total') {
      const num = +data;
      if (!num || isNaN(num)) {
        return 0.0;
      }
      return getCurrencyWithSymbol(
        storeData?.cart?.cartData?.currencyCode || 'USD',
        num,
        false,
      );
    }
    if (key === 'currency_code_symbol') {
      return getCurrencySymbol(data || 'USD');
    }
    if (key === 'total_savings') {
      if (data && typeof data === 'object') {
        const data1 = data as Cart;
        const subtotal = parseFloat(data1?.SubtotalAmount);
        const total = parseFloat(data1?.TotalAmount);
        const originalShippingCost = parseFloat(
          data1?.Fees?.shipping?.originalCost,
        );
        const salesTax = parseFloat(data1?.SalesTaxAmount ?? '0');
        const calculatedFee =
          data1?.Fees?.CalculatedFees?.reduce(
            (acc: number, fee: ICalculatedFee) =>
              acc + parseFloat(fee?.ComputedAmount || '0'),
            0,
          ) || 0;

        // Calculate item-level discounts
        const itemLevelDiscounts = Array.isArray(data1.LineItems)
          ? data1.LineItems.reduce((acc, item) => {
              const originalPrice = parseFloat(item?.OriginalPrice);
              const unitPrice = parseFloat(item?.UnitPrice);
              const quantity = item?.Quantity || 0;

              // Only calculate discount if originalPrice is greater than unitPrice
              const discount =
                originalPrice > unitPrice
                  ? (originalPrice - unitPrice) * quantity
                  : 0;

              return acc + discount;
            }, 0)
          : 0;

        const expectedTotalBeforeDiscounts =
          subtotal + originalShippingCost + salesTax + calculatedFee;
        const savings =
          expectedTotalBeforeDiscounts - total + itemLevelDiscounts;
        return savings.toFixed(2);
      }
    }
    if (key === 'currency_code') {
      return data || 'USD';
    }

    return data ? String(data) : '';
  });

  return interpolatedText;
};

export const storePhoneCode = (phoneCode: string) => {
  if (
    phoneCode &&
    typeof window !== 'undefined' &&
    typeof localStorage !== 'undefined'
  )
    localStorage.setItem('phoneCode', phoneCode);
};

export const getPhoneCode = () => {
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined')
    return localStorage.getItem('phoneCode');
  return '';
};

// The below code is removing inline styles and external styles from the content}
function removeStyling(text: string) {
  return text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
}

const removeInlineStyles = (inputString: string) => {
  return inputString.replace(/style="[^"]*"/g, '');
};

// Function to remove external styles like font, size, color, background
const removeExternalStyles = (inputString: string) => {
  const sanitizedString = inputString.replace(
    /<(.*?)[^>]*style="[^"]*"[^>]*>/g,
    '<$1>',
  );
  return sanitizedString.replace(/<(font|span)[^>]*>/g, '');
};

// Example of another operation to remove script or unwanted tags
const removeUnwantedTags = (inputString: string) => {
  return inputString.replace(/<(script|style)[^>]*>.*?<\/\1>/g, '');
};

// Example function to replace URLs with clickable links
const convertLinksToClickable = (inputString: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return inputString.replace(urlRegex, '<a href="$1" target="_blank">$1</a>');
};

export const handleSanitizeContent = (data: string) => {
  let sanitizedValue = data;

  // First remove inline styles

  sanitizedValue = removeStyling(sanitizedValue);
  sanitizedValue = removeInlineStyles(sanitizedValue);

  // Remove external styles
  sanitizedValue = removeExternalStyles(sanitizedValue);

  // Remove unwanted tags like script or style
  sanitizedValue = removeUnwantedTags(sanitizedValue);

  // Convert URLs to clickable links
  sanitizedValue = convertLinksToClickable(sanitizedValue);

  return sanitizedValue; // Update the state with sanitized content
};

//remove unwanted words using placeholder styles add

export const convertToSnakeCase = (str: string) => {
  // Extract the first word, split by spaces and take the first element
  const firstWord = str?.split(/[^\w]+/)[0];

  // Convert the first word to lowercase and replace spaces with underscores
  return firstWord?.toLowerCase()?.replace(/\s+/g, '_'); // (this isn't really needed, since we're only using the first word)
};

export const getFontSize = (value?: string) => {
  if (!value) return;
  else {
    if (value === 'auto') {
      return 'auto';
    } else if (value.includes('%')) {
      return value; // If it's a percentage (e.g., '10%'), apply it directly
    } else {
      return `${value}px`; // If it's a number (e.g., '10'), apply '10px'
    }
  }
};

export const mapPercentageToRange = (percentage: string) => {
  const percent = parseInt(percentage);
  if (percent < 1 || percent > 100) {
    throw new Error('Percentage must be between 1 and 100');
  }

  const inputMin = 1;
  const inputMax = 100;
  const outputMin = 25;
  const outputMax = 55;

  // Linear mapping + rounding
  const mappedValue =
    outputMin +
    ((percent - inputMin) / (inputMax - inputMin)) * (outputMax - outputMin);

  return Math.round(mappedValue);
};
export const restrictEmojis = (e: ChangeEvent<HTMLInputElement>) =>
  e.target.value?.replace(/[^\x20-\x7E]/g, '')?.trimStart();

function similarity(a: string, b: string) {
  a = a.toLowerCase();
  b = b.toLowerCase();
  const longer = a.length > b.length ? a : b;
  const shorter = a.length > b.length ? b : a;
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  const same = longerLength - editDistance(longer, shorter);
  return same / longerLength;
}

function editDistance(s1: string, s2: string) {
  const costs = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) costs[j] = j;
      else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1))
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[s2.length] = lastValue;
  }
  return costs[s2.length];
}

export function findClosestState(userInput: string) {
  let bestMatch = null;
  let bestScore = 0;

  for (const s of stateList) {
    const score = similarity(userInput, s.name);
    if (score > bestScore) {
      bestScore = score;
      bestMatch = s.code;
    }
  }

  return bestScore > 0.6 ? bestMatch : null;
}
